import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import {
  MAX_ADMIN_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PASSWORD_LENGTH,
} from '@/api/types/tenants';

// Create mirrors the legacy CreateModal.cshtml: Name, Admin Email Address,
// Admin Password (+ eye toggle) and a repeater of connection strings
// (Name + Connection String). Edit mirrors TenantUpdateDto: Name only.

const connectionStringSchema = z.object({
  name: z.string().trim(),
  value: z.string().trim(),
});

const createSchema = z.object({
  name: z.string().trim().min(1, 'Client name is required.').max(MAX_NAME_LENGTH),
  adminEmailAddress: z
    .string()
    .trim()
    .min(1, 'Admin email address is required.')
    .email('Please enter a valid email address.')
    .max(MAX_ADMIN_EMAIL_LENGTH),
  adminPassword: z
    .string()
    .min(1, 'Admin password is required.')
    .max(MAX_PASSWORD_LENGTH),
  connectionStrings: z.array(connectionStringSchema),
});

const editSchema = z.object({
  name: z.string().trim().min(1, 'Client name is required.').max(MAX_NAME_LENGTH),
});

export type TenantCreateFormValues = z.infer<typeof createSchema>;
export type TenantEditFormValues = z.infer<typeof editSchema>;

interface CreateProps {
  show: boolean;
  mode: 'create';
  busy?: boolean;
  onSubmit: (values: TenantCreateFormValues) => void;
  onClose: () => void;
}

interface EditProps {
  show: boolean;
  mode: 'edit';
  initialName: string;
  busy?: boolean;
  onSubmit: (values: TenantEditFormValues) => void;
  onClose: () => void;
}

type TenantFormModalProps = CreateProps | EditProps;

export function TenantFormModal(props: TenantFormModalProps) {
  if (props.mode === 'create') return <CreateForm {...props} />;
  return <EditForm {...props} />;
}

function CreateForm({ show, busy = false, onSubmit, onClose }: CreateProps) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TenantCreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      adminEmailAddress: '',
      adminPassword: '',
      connectionStrings: [{ name: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'connectionStrings' });

  return (
    <Modal
      show={show}
      title="New Client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="tenant-create-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="tenant-create-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Client Name"
          maxLength={MAX_NAME_LENGTH}
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="Admin Email Address"
          type="email"
          autoComplete="off"
          maxLength={MAX_ADMIN_EMAIL_LENGTH}
          error={errors.adminEmailAddress?.message}
          {...register('adminEmailAddress')}
        />

        <div className="mb-3">
          <label className="form-label" htmlFor="adminPassword">
            Admin Password <span>*</span>
          </label>
          <div className="input-group">
            <input
              id="adminPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-control${errors.adminPassword ? ' is-invalid' : ''}`}
              maxLength={MAX_PASSWORD_LENGTH}
              autoComplete="new-password"
              {...register('adminPassword')}
            />
            <button
              className="btn btn-light"
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`fa ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
            </button>
          </div>
          {errors.adminPassword && (
            <div className="text-danger small mt-1">{errors.adminPassword.message}</div>
          )}
        </div>

        <label className="form-label">Connection Strings</label>
        {fields.map((field, index) => (
          <div className="row" key={field.id}>
            <div className="col-4">
              <TextField label="Name" {...register(`connectionStrings.${index}.name`)} />
            </div>
            <div className="col-6">
              <TextField
                label="Connection String"
                {...register(`connectionStrings.${index}.value`)}
              />
            </div>
            <div className="col-2 d-flex justify-content-center align-items-start">
              <button
                type="button"
                className="btn btn-sm btn-danger mt-4"
                onClick={() => remove(index)}
                disabled={fields.length === 1}
                aria-label="Remove connection string"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
          </div>
        ))}
        <div className="row">
          <button
            type="button"
            className="btn btn-primary col"
            onClick={() => append({ name: '', value: '' })}
          >
            <i className="fa fa-plus" />
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditForm({ show, initialName, busy = false, onSubmit, onClose }: EditProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TenantEditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: initialName },
  });

  return (
    <Modal
      show={show}
      title="Edit Client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="tenant-edit-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="tenant-edit-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Client Name"
          maxLength={MAX_NAME_LENGTH}
          error={errors.name?.message}
          {...register('name')}
        />
      </form>
    </Modal>
  );
}
